import { useState, useEffect, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import AuthContext from "./AuthContext";
import { supabase } from "../../lib/supabase";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [providerToken, setProviderToken] = useState<string | null>(null);

  // Initialize and set up auth listener
  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        setIsLoading(true);

        // Get current session
        const { data, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) throw sessionError;

        if (data.session) {
          console.log("Initial session data:", {
            provider_token: data.session.provider_token
              ? "Present"
              : "Not Present",
            provider_refresh_token: data.session.provider_refresh_token
              ? "Present"
              : "Not Present",
            access_token: data.session.access_token ? "Present" : "Not Present",
            refresh_token: data.session.refresh_token
              ? "Present"
              : "Not Present",
          });

          setSession(data.session);
          setUser(data.session.user);
          setProviderToken(data.session.provider_token || null);

          // Refresh session if token is about to expire
          const expiresAt = data.session.expires_at;
          const timeNow = Math.floor(Date.now() / 1000);

          // If token expires in less than 60 minutes
          if (expiresAt && expiresAt - timeNow < 3600) {
            const { data: refreshData, error: refreshError } =
              await supabase.auth.refreshSession();

            if (!refreshError && refreshData.session) {
              console.log("Refreshed session data:", {
                provider_token: refreshData.session.provider_token
                  ? "Present"
                  : "Not Present",
                provider_refresh_token: refreshData.session
                  .provider_refresh_token
                  ? "Present"
                  : "Not Present",
              });

              setSession(refreshData.session);
              setUser(refreshData.session.user);
              setProviderToken(refreshData.session.provider_token || null);
            }
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        setError(
          error instanceof Error
            ? error.message
            : "Failed to initialize authentication"
        );
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Set up auth state change listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Only log important state changes
      if (event !== "TOKEN_REFRESHED") {
        console.log("Auth state changed:", event);
      }

      if (newSession) {
        // Don't update state if only the token changed and user remains the same
        const sessionChanged =
          !session ||
          session.expires_at !== newSession.expires_at ||
          session.user?.id !== newSession.user?.id;

        if (sessionChanged) {
          setSession(newSession);
          setUser(newSession.user);
          setProviderToken(newSession.provider_token || null);
        }
      } else {
        setSession(null);
        setUser(null);
        setProviderToken(null);
      }

      setIsLoading(false);
    });

    // Clean up subscription
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Build redirect URL
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = "5173";

      // Create appropriate redirect URL
      let redirectUrl;
      if (hostname === "localhost") {
        redirectUrl = `${protocol}//${hostname}${port}/auth/callback`;
      } else {
        // For production, ensure it's using app subdomain
        const baseDomain = hostname.replace(/^(app|www)\./, "");
        redirectUrl = `${protocol}//app.${baseDomain}${port}/auth/callback`;
      }

      console.log("Using redirect URL:", redirectUrl);

      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          scopes:
            "https://mail.google.com/ https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.metadata",
          redirectTo: redirectUrl,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });

      if (signInError) throw signInError;
    } catch (error) {
      console.error("Google sign-in error:", error);
      setError(
        error instanceof Error ? error.message : "Failed to sign in with Google"
      );
      setIsLoading(false);
    }
  };

  // Sign in with Microsoft
  const signInWithMicrosoft = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Similar URL construction as with Google
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port ? `:${window.location.port}` : "";

      let redirectUrl;
      if (hostname === "localhost") {
        redirectUrl = `${protocol}//${hostname}${port}/auth/callback`;
      } else {
        const baseDomain = hostname.replace(/^(app|www)\./, "");
        redirectUrl = `${protocol}//app.${baseDomain}/auth/callback`;
      }

      const { error: signInError } = await supabase.auth.signInWithOAuth({
        provider: "azure",
        options: {
          scopes:
            "email profile offline_access https://outlook.office.com/mail.read https://outlook.office.com/mail.readwrite",
          redirectTo: redirectUrl,
        },
      });

      if (signInError) throw signInError;
    } catch (error) {
      console.error("Microsoft sign-in error:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to sign in with Microsoft"
      );
      setIsLoading(false);
    }
  };

  // Refresh provider token
  const refreshProviderToken = async (): Promise<string | null> => {
    try {
      console.log("Attempting to refresh provider token");

      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        throw error;
      }

      if (data.session) {
        console.log(
          "Session refreshed. Provider token:",
          data.session.provider_token ? "Present" : "Not Present"
        );

        setSession(data.session);
        setUser(data.session.user);
        const newToken = data.session.provider_token || null;
        setProviderToken(newToken);

        return newToken;
      } else {
        console.log("No session after refresh");
        return null;
      }
    } catch (error) {
      console.error("Failed to refresh provider token:", error);
      return null;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { error: signOutError } = await supabase.auth.signOut();

      if (signOutError) throw signOutError;

      // Clear local state
      setUser(null);
      setSession(null);
      setProviderToken(null);
    } catch (error) {
      console.error("Sign-out error:", error);
      setError(error instanceof Error ? error.message : "Failed to sign out");
    } finally {
      setIsLoading(false);
    }
  };

  // Get current provider token, refreshing if needed
  const getProviderToken = async (): Promise<string | null> => {
    if (providerToken) {
      return providerToken;
    }

    // Try to refresh the token
    const refreshedToken = await refreshProviderToken();
    return refreshedToken || null;
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        isLoading,
        signInWithGoogle,
        signInWithMicrosoft,
        signOut,
        error,
        providerToken,
        getProviderToken,
        refreshProviderToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
